import {
  BridgeMessage,
  KeyboardStatusChangedEvent,
  MessageDetails,
  MessageType,
  NavigationRequest,
} from "@shared/services/webview/types";
import { ApiTags, Notification } from "@shared/types";
import { useEffect, useState } from "react";
import {
  postMessageToNative,
  userUpdated,
} from "services/webview/webViewBridgeUtils";
import { useHistory } from "react-router-dom";
import { useNotificationNavigation } from "features/notifications/hooks.ts";
import { openMessage } from "utils/misc.tsx";
import { useSelector } from "react-redux";
import { IS_WEB_VIEW } from "common/constants.ts";
import { useRefValue } from "@shared/utils/hooks/misc.ts";
import { useHandleNavigateBack } from "services/webview/hooks.ts";
import {
  logSecurityEvent,
  sanitizeString,
  SECURITY_CONFIG,
  validateMessage,
  validateNavigationRequest,
} from "services/webview/webViewSecurity.ts";
import * as Sentry from "@sentry/react";
import { useAppDispatch } from "app/hooks.ts";
import { sharedLib } from "@shared/services/shared.service.ts";

export const useWebViewBridge = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const historyRef = useRefValue(useHistory());
  const { navigateFromNotification } = useNotificationNavigation();
  const realUser = useSelector(
    sharedLib.selectors.authentication.selectRealUser
  );
  const { navigateBack } = useHandleNavigateBack();
  const dispatch = useAppDispatch();

  useEffect(() => {
    userUpdated(realUser).catch(() => {});
  }, [realUser]);

  useEffect(() => {
    if (isInitialized || !IS_WEB_VIEW) {
      return;
    }

    window.dispatchWebViewEvent = handleNativeMessage;
    postMessageToNative({ type: MessageType.WEBVIEW_READY });
    setIsInitialized(true);
  }, [isInitialized]);

  const handleNativeMessage = (message: BridgeMessage<any>): void => {
    if (!validateMessage(message)) {
      logSecurityEvent("Message validation failed", {
        type: message?.type,
        origin: window.location.origin,
      });
      return;
    }

    const { type, payload } = message;

    switch (type) {
      case MessageType.NAVIGATE_BACK_REQUEST:
        navigateBack();
        break;

      case MessageType.KEYBOARD_STATUS_CHANGED:
        dispatch(
          sharedLib.slices.common.actions.setWebviewKeyboardStatus(
            (payload as KeyboardStatusChangedEvent).keyboardStatus
          )
        );
        break;

      case MessageType.NAVIGATION_REQUEST:
        const pathname = (payload as NavigationRequest)?.pathname;
        if (pathname && validateNavigationRequest(pathname)) {
          historyRef.current?.push(pathname);
        }
        break;

      case MessageType.OPEN_MESSAGE:
        const messageDetails = payload as MessageDetails;
        if (messageDetails) {
          const sanitizedText = sanitizeString(messageDetails.text);
          if (sanitizedText.length <= SECURITY_CONFIG.MAX_TEXT_MESSAGE_SIZE) {
            openMessage(sanitizedText, messageDetails.type);
          } else {
            Sentry.captureMessage(
              `Skipped opening message because it was too long. Message: ${sanitizedText},`,
              "warning"
            );
          }
        }
        break;

      case MessageType.NOTIFICATION_NAVIGATION_REQUEST:
        navigateFromNotification(payload as Notification);
        break;

      case MessageType.SUBSCRIPTION_APPROVED:
        dispatch(
          sharedLib.sharedApi.authentication.util.invalidateTags([
            { type: ApiTags.USER, id: "LIST" },
          ])
        );
        dispatch(
          sharedLib.sharedApi.settings.util.invalidateTags([
            { type: ApiTags.RECENT_PAYMENTS, id: "LIST" },
          ])
        );
        break;

      case MessageType.RESPONSE:
        // Handled by the injected JavaScript promise resolution layer
        break;

      default:
        logSecurityEvent("Unknown message type received", { type });
    }
  };

  return {
    isInitialized,
  };
};
